import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FLAVOURS } from "@/data/content";
import { EASE } from "@/lib/scroll";

const N = FLAVOURS.length;
// slot by offset from the active bag: -2 far left ... 0 front ... 2 far right
const SLOT = {
  "-2": { x: "-74%", y: "-4%", scale: 0.5, rotate: -16, opacity: 0.6, zIndex: 1 },
  "-1": { x: "-44%", y: "-1%", scale: 0.7, rotate: -9, opacity: 0.92, zIndex: 2 },
  0: { x: "0%", y: "4%", scale: 1, rotate: 0, opacity: 1, zIndex: 5 },
  1: { x: "44%", y: "-1%", scale: 0.7, rotate: 9, opacity: 0.92, zIndex: 2 },
  2: { x: "74%", y: "-4%", scale: 0.5, rotate: 16, opacity: 0.6, zIndex: 1 },
};
const offset = (i, a) => ((i - a + N + 2) % N) - 2;
const BAG_W = 0.46;
const CENTER = Object.fromEntries([-2, -1, 0, 1, 2].map((k) => [k, 0.5 + (parseFloat(SLOT[k].x) / 100) * BAG_W]));

const Controls = ({ active, go }) => (
  <div className="absolute bottom-0 inset-x-0 z-10 flex items-center justify-center gap-3">
    <button data-testid="hero-bag-prev" aria-label="Previous flavour" onClick={() => go(active - 1)}
      className="h-10 w-10 grid place-items-center rounded-full bg-white border border-[#1B1530]/10 hover:bg-[#1B1530] hover:text-white transition-colors"><ChevronLeft className="h-4 w-4" /></button>
    <div className="relative h-10 min-w-[200px] overflow-hidden rounded-full bg-white border border-[#1B1530]/10 px-5">
      <AnimatePresence initial={false}>
        <motion.p key={active} data-testid="hero-bag-name" className="absolute inset-0 grid place-items-center font-display text-lg whitespace-nowrap"
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }} transition={{ duration: 0.35 }}>
          {FLAVOURS[active].name}
        </motion.p>
      </AnimatePresence>
    </div>
    <button data-testid="hero-bag-next" aria-label="Next flavour" onClick={() => go(active + 1)}
      className="h-10 w-10 grid place-items-center rounded-full bg-white border border-[#1B1530]/10 hover:bg-[#1B1530] hover:text-white transition-colors"><ChevronRight className="h-4 w-4" /></button>
  </div>
);

export const HeroBags = ({ rx, ry, progress }) => {
  const [active, setActive] = useState(2);
  const [auto, setAuto] = useState(true);
  const y = useTransform(progress, [0, 1], [0, -110]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % N), 4000);
    return () => clearInterval(t);
  }, [auto]);
  const go = (i) => { setAuto(false); setActive(((i % N) + N) % N); };
  const startX = useRef(null);
  const onDown = (e) => { startX.current = e.clientX; };
  // Pick by tap position: 3D tilt makes DOM hit-testing unreliable, so map x to the nearest slot
  const onUp = (e) => {
    if (startX.current === null) return;
    const dx = e.clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) > 40) { go(active + (dx < 0 ? 1 : -1)); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const o = [-2, -1, 0, 1, 2].reduce((best, k) => (Math.abs(CENTER[k] - fx) < Math.abs(CENTER[best] - fx) ? k : best), 0);
    if (o !== 0) go(active + o);
  };
  const f = FLAVOURS[active];

  return (
    <div data-testid="hero-bag-carousel" className="md:col-start-7 md:col-span-6 md:row-start-1 md:row-span-2 md:self-center relative h-[300px] min-[400px]:h-[340px] sm:h-[560px] md:h-[500px] lg:h-[640px] -mt-2 md:mt-0" style={{ perspective: 1200 }}>
      <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1, backgroundColor: `${f.color}55` }} transition={{ duration: 0.9, ease: EASE }}
        className="blob absolute left-[8%] right-[8%] top-[2%] aspect-square max-h-[84%] mx-auto" />
      <motion.div onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => { startX.current = null; }}
        style={{ rotateX: rx, rotateY: ry, y, transformStyle: "preserve-3d" }} className="absolute inset-0 bottom-14 touch-pan-y select-none">
        {FLAVOURS.map((b, i) => {
          const o = offset(i, active);
          return (
            <motion.button key={b.key} type="button" data-testid={`hero-bag-${b.key}`} aria-label={`Show ${b.name}`} onClick={(e) => { if (e.detail === 0) go(i); }}
              className="absolute left-[27%] top-[6%] w-[46%] focus:outline-none" initial={false}
              animate={SLOT[o]} transition={{ type: "spring", stiffness: 140, damping: 20 }}>
              <img src={b.img} alt={`${b.name} bag`} draggable={false} data-testid={o === 0 ? "hero-product-image" : undefined}
                className={`w-full drop-shadow-[0_30px_35px_rgba(27,21,48,0.3)] ${o === 0 ? "float-a" : ""}`} />
            </motion.button>
          );
        })}
      </motion.div>
      <Controls active={active} go={go} />
    </div>
  );
};
